<template>
  <div class="e-data-items" :class="{ disabled: hasLoadingRow }">
    <e-data-table
      v-if="!$vuetify.display.smAndDown"
      v-model="selected"
      :headers="headers"
      :items="filteredItems"
      :item-value="itemValue"
      :return-object="returnObject"
      :row-props="applyedRowProps"
      :interactive="interactive"
      :sticky-header-top="stickyHeaderTop"
      :styled-header="styledHeader"
      :loading="loading"
      :content-wrap="contentWrap"
      @on-row-click="onRowClick"
    >
      <template v-for="(header, index) in editableHeaders" :key="index" #[`header.${header.key}`]>
        <data-table-editable-t-h
          :ref="massOperationRef"
          :title="header.editable!.title || ''"
          @start="startMassOperation"
          @end="endMassOperation"
        >
          <e-input
            v-if="header.editable!.type === EditableHeaderType.Input"
            v-model:number-value="header.editable!.value as number | null"
            autofocus
            :maska="header.editable!.maska"
            :label="header.editable!.title"
            @keydown.enter="massOperationUpdate(header.editable)"
            @blur="massOperationUpdate(header.editable)"
          />

          <e-checkbox
            v-if="header.editable!.type === EditableHeaderType.Checkbox"
            :model-value="checkSelectedCounts(header.editable!.editableKey).all"
            :square-selected="checkSelectedCounts(header.editable!.editableKey).partial"
            :label="header.editable!.title"
            @update:model-value="massOperationCheckboxUpdate(header.editable, $event)"
          />

          <e-select
            v-if="header.editable!.type === EditableHeaderType.Select && header.editable.selectOptions"
            v-model="header.editable!.value"
            item-value="value"
            :label="header.editable!.title"
            :items="header.editable.selectOptions"
            @item-click="massOperationSelectUpdate(header.editable)"
          />
        </data-table-editable-t-h>
      </template>

      <template v-if="disabled" v-slot:[`header.data-table-select`]="{ someSelected, allSelected }">
        <e-checkbox :model-value="allSelected" :square-selected="someSelected" disabled />
      </template>
      <template v-if="disabled" v-slot:[`item.data-table-select`]="{ isSelected, internalItem }">
        <e-checkbox :model-value="isSelected(internalItem)" disabled />
      </template>
      <template v-for="(_, slotName) in slots" v-slot:[slotName]="slotData">
        <slot :name="slotName" v-bind="slotData as any" />
      </template>
    </e-data-table>

    <template v-else>
      <mobile-items-list
        v-model="selected"
        :headers="headers"
        :items="filteredItems"
        :item-value="itemValue"
        :return-object="returnObject"
        :disabled="disabled"
        :row-props="applyedRowProps"
        :loading="loading"
        @on-row-click="onRowClick"
      >
        <template v-if="editableHeaders.length" #mobile-header-append>
          <span
            class="d-flex align-center input-placeholder link"
            :class="{ 'text-inactive': loading }"
            @click="startMassOperation"
          >
            <e-icon name="editPen" :size="20" class="mr-1" />
            Радактировать выбранные
          </span>
        </template>

        <template v-for="(_, slotName) in slots" v-slot:[slotName]="slotData">
          <slot :name="slotName" v-bind="slotData as any" />
        </template>
      </mobile-items-list>

      <mass-operations-dialog
        ref="massOperationsDialogRef"
        v-model="selected"
        :disabled="!loading"
        :editable-headers="editableHeaders"
        :check-selected-counts="checkSelectedCounts"
      />
    </template>

    <cover-loader :shown="!!disableLoader" />
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, unknown>, K extends Paths<T> | undefined">
import { type ComponentPublicInstance, computed, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import type { EDataItemsOnRowClickItem } from '@/core/types/components/components';
import type { Paths } from '@/core/types/gengeric';
import DataTableEditableTH from '@/core/components/common/data-items/DataTableEditableTH.vue';
import MassOperationsDialog from '@/core/components/common/data-items/MassOperationsDialog.vue';
import MobileItemsList from '@/core/components/common/data-items/MobileItemsList.vue';
import CoverLoader from '@/core/components/interface/CoverLoader.vue';
import { ECheckbox, EDataTable, EIcon, EInput, ESelect } from '@/core/components/ui';
import {
  type DataTableHeaders,
  type EditableHeader,
  EditableHeaderType,
  type RowProps,
  type TableSlots,
} from '@/core/types/components/table';
import { getNestedObjectProperty, setNestedProperty } from '@/core/utils/object';
import { isObject } from '@/core/utils/type-guards';

const props = withDefaults(
  defineProps<{
    items: T[];
    headers: DataTableHeaders<T>;
    itemValue?: K;
    returnObject?: boolean;
    showOnlySelected?: boolean;
    disabled?: boolean;
    interactive?: boolean;
    styledHeader?: boolean;
    stickyHeaderTop?: string;
    rowProps?: RowProps<T>;
    loading?: boolean;
    contentWrap?: boolean;
    disableLoader?: boolean;
  }>(),
  {
    contentWrap: true,
  },
);

const emit = defineEmits<{
  (event: 'onRowClick', e: Event, row: EDataItemsOnRowClickItem<T>): void;
}>();

const $display = useDisplay();

const slots = defineSlots<TableSlots<T>>();

const selected = defineModel<K extends Paths<T> ? T[K][] : T[]>();

const styledHeader = ref(props.styledHeader);

const applyedRowProps = (data: { item: T }) => {
  const rProps = isObject(props.rowProps)
    ? props.rowProps
    : typeof props.rowProps === 'function'
      ? props.rowProps(data)
      : {};

  if (isObject(rProps) && data.item._loadingRow) {
    if ('class' in rProps) {
      rProps.class = `${rProps.class} disable-loader`;
    } else {
      rProps.class = 'disable-loader';
    }
  }

  return rProps;
};

const hasLoadingRow = computed(() => props.items.some((item) => item._loadingRow));

const filteredItems = computed(() => {
  if (!props.showOnlySelected || !selected.value) {
    return props.items;
  }

  return props.items.filter((item) => {
    if (props.itemValue) {
      const value = getNestedObjectProperty(item, props.itemValue);
      if (!value) {
        return false;
      }

      return selected.value!.includes(value);
    }

    return (selected.value as T[]).includes(item);
  });
});

const onRowClick = (e: Event, row: EDataItemsOnRowClickItem<T>) => {
  if (hasLoadingRow.value) {
    return;
  }

  emit('onRowClick', e, row);
};

const editableHeaders = ref();

const updateEditableHeaders = () => {
  editableHeaders.value = props.headers
    .filter((header) => header.editable !== undefined && header.editable && !header.editable.disabled)
    .map((header) => ({
      ...header,
      editable: {
        ...header.editable,
        type: header.editable!.type ?? EditableHeaderType.Input,
        title: header.editable!.title ?? '',
        editableKey: header.editable!.editableKey ?? '',
        value: header.editable!.value ?? null,
        isDirty: false,
      },
    }));
};

watch(
  () => props.headers,
  () => {
    updateEditableHeaders();
  },
  { deep: true, immediate: true },
);

const massOperationUpdate = (editable?: EditableHeader) => {
  if (!selected.value || !editable || !editable.editableKey || editable.value === null) {
    if (editable?.type === EditableHeaderType.Input) {
      endMassOperation();
    }
    return;
  }

  selected.value.forEach((item) => {
    setNestedProperty(item as Record<string, unknown>, editable.editableKey, editable.value);
  });

  if (editable.type === EditableHeaderType.Input) {
    endMassOperation();
  }
};

const massOperationCheckboxUpdate = (editable: EditableHeader, value: boolean) => {
  if (!selected.value || !editable || !editable.editableKey) {
    return;
  }

  editable.value = value;

  massOperationUpdate(editable);
};

const massOperationSelectUpdate = (editable: EditableHeader) => {
  if (!selected.value || !editable || !editable.editableKey) {
    return;
  }

  massOperationUpdate(editable);
};

const massOperationRefs: ComponentPublicInstance<{ stopEdit: () => void }>[] = [];
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const massOperationRef = (el: any) => {
  massOperationRefs.push(el);
};
const massOperationsDialogRef = ref();

const startMassOperation = () => {
  if (props.loading) {
    return;
  }

  styledHeader.value = true;

  if ($display.smAndDown.value) {
    setTimeout(() => {
      massOperationsDialogRef.value?.show();
    });
  }
};

const endMassOperation = () => {
  styledHeader.value = false;
  massOperationRefs.forEach((ref) => {
    ref.stopEdit();
  });
};

const checkSelectedCounts = (key: Paths<T>) => {
  if (!selected.value || props.itemValue) {
    return {
      partial: false,
      all: false,
    };
  }

  const itemsWithActiveKey = (selected.value as T[]).filter((x) => getNestedObjectProperty(x, key));

  return {
    partial: !!(itemsWithActiveKey.length && itemsWithActiveKey.length !== selected.value.length),
    all: itemsWithActiveKey.length === selected.value.length,
  };
};
</script>

<style scoped lang="scss">
.e-data-items {
  position: relative;

  &.disabled {
    cursor: progress;
    & > * {
      pointer-events: none;
    }
  }
}
</style>
